import {
  AlertDangerIcon,
  ButtonIcon,
  Caption,
  css,
  DuplicateIcon,
  EllipsisText,
  PlusIcon,
  Stack,
  styled,
  Tooltip,
  TrashIcon,
} from 'canoe';
import { nanoid } from 'nanoid';
import React from 'react';
import { useDispatch } from 'react-redux';
import { s3Image } from 'site-constants';

import { DraggableValueCard, ValueGroup } from '@candu-ai/craft/ui';
import { useNodeDisplayName } from 'craft/hooks';
import { setPaletteLayersHidden, useFlow, useFlowDelete, useFlowGraph } from 'craft/state';
import { SerializedNodeTree } from 'craft/types';

import { PreviewFlow } from './PreviewFlow';

const ActionButtonsWrapper = styled(Stack)`
  ${({ theme }) => css`
    opacity: 0;
    transition: ${theme.transitions.default()};
  `}
`;

const StepContentWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  overflow: hidden;

  &:hover {
    ${ActionButtonsWrapper} {
      opacity: 1;
    }
  }
`;

const StepThumbnailWrapper = styled.div`
  ${({ theme }) => css`
    width: 134px;
    height: 64px;
    flex-shrink: 0;
    position: relative;
    border-radius: ${theme.borderRadius.small};
    border: 1px solid ${theme.colors.GlitterGray};
    overflow: hidden;
  `}
`;

const StepThumbnail = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StepLabelWrapper = styled.div`
  ${({ theme }) => css`
    flex-grow: 1;
    display: flex;
    align-items: center;
    margin-left: ${theme.spaces.xs};
    overflow: hidden;
  `}
`;

interface Props {
  nodeId: string;
  index: number;
}

const StepName = ({ nodeId, index }: Props) => {
  let displayName = useNodeDisplayName(nodeId);

  if (displayName === 'Flow Step') {
    displayName = `Step ${index + 1}`;
  }

  return (
    <Caption
      mb="none"
      element="label"
      css={{
        maxWidth: '100%',
        display: 'flex',
        flexShrink: 1,
        minWidth: 0,
      }}
    >
      <EllipsisText content={displayName}>
        <span>{displayName}</span>
      </EllipsisText>
    </Caption>
  );
};

interface StepCardsProps {
  nodeId: string;
  createStep: (nodeId: string) => SerializedNodeTree;
}

export const StepCards = ({ nodeId, createStep }: StepCardsProps) => {
  const { isStepConnected } = useFlowGraph(nodeId);
  const { canDeleteSteps, onDeleteStepId } = useFlowDelete(nodeId);
  const { stepIds, index, onSetStepId, onMoveStep, onDuplicateStep, onAddStep, flowNode } = useFlow(
    nodeId,
  );
  const dispatch = useDispatch();

  const connectedStepIds = stepIds.filter((stepId) => isStepConnected(stepId));

  return (
    <ValueGroup>
      {stepIds.map((stepId, stepIndex) => (
        <DraggableValueCard
          key={stepId}
          index={stepIndex}
          padding="xxs"
          isActive={index === stepIndex}
          onClick={() => onSetStepId(stepId)}
          onDrop={onMoveStep}
          placeholderImagePath={s3Image('/svg/toolbox-drag-placeholder-flow-steps.svg')}
          isHoverable
          horizontalSpace="xxs"
          draggableHandleHeight={4}
          onDrag={(isDragging: boolean) => {
            dispatch(setPaletteLayersHidden(isDragging));
          }}
        >
          <StepContentWrapper>
            <StepThumbnailWrapper>
              <StepThumbnail>
                <PreviewFlow stepNodeId={flowNode.data.nodes[stepIndex]} />
              </StepThumbnail>
            </StepThumbnailWrapper>
            <StepLabelWrapper>
              {!connectedStepIds.includes(stepId) && (
                <Tooltip
                  state="danger"
                  placement="left"
                  offset="xxs"
                  content={
                    <span>
                      This step is not linked to any other steps.
                      <br />
                      Use interactions to connect Steps.
                    </span>
                  }
                >
                  <AlertDangerIcon color="Danger" mr="xs" />
                </Tooltip>
              )}
              <StepName index={stepIndex} nodeId={flowNode.data.nodes[stepIndex]} />
            </StepLabelWrapper>
            <ActionButtonsWrapper justify="center" space="none">
              {canDeleteSteps && (
                <Tooltip placement="top" offset="xxs" content="Delete step">
                  <ButtonIcon
                    icon={TrashIcon}
                    color="solid"
                    size="xsmall"
                    onClick={(e: MouseEvent) => {
                      // need to stop propagation or a select event is triggered for deleted step
                      e.stopPropagation();
                      onDeleteStepId(stepId);
                    }}
                    radius="small"
                    ml="xxs"
                  />
                </Tooltip>
              )}
              <Tooltip placement="top" offset="xxs" content="Duplicate step">
                <ButtonIcon
                  icon={DuplicateIcon}
                  color="solid"
                  size="xsmall"
                  onClick={(e: MouseEvent) => {
                    // need to stop propagation or a select event is triggered for duplicated step
                    e.stopPropagation();
                    onDuplicateStep(stepId);
                  }}
                  radius="small"
                  ml="xxs"
                />
              </Tooltip>
            </ActionButtonsWrapper>
          </StepContentWrapper>
        </DraggableValueCard>
      ))}

      <ButtonIcon
        fullWidth
        color="contrast"
        radius="small"
        onClick={() => {
          onAddStep(createStep(nanoid(6)));
        }}
        icon={PlusIcon}
        inverted
        mt="xxs"
      />
    </ValueGroup>
  );
};
